/**
 * Horizon+ content components: toast, wishlist, countdown, tabs, stories,
 * delivery estimate, text slider, cart reservation timer and promo popup.
 */

/**
 * @param {string} name
 * @param {CustomElementConstructor} constructor
 */
const define = (name, constructor) => {
  if (!customElements.get(name)) customElements.define(name, constructor);
};

const isDesignMode = () => Boolean(window.Shopify?.designMode);

/**
 * Reads a JSON value from a storage area without throwing when storage is blocked.
 * @param {Storage} storage
 * @param {string} key
 * @param {any} fallback
 */
function readStorage(storage, key, fallback) {
  try {
    const raw = storage.getItem(key);
    return raw === null ? fallback : JSON.parse(raw);
  } catch (_) {
    return fallback;
  }
}

/**
 * @param {Storage} storage
 * @param {string} key
 * @param {any} value
 */
function writeStorage(storage, key, value) {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (_) {
    // Storage can be unavailable (private mode, blocked site data); the feature degrades silently.
  }
}

/* -------------------------------------------------------------------------- */
/* Toast                                                                      */
/* -------------------------------------------------------------------------- */

/** @type {number | undefined} */
let toastTimeout;

/**
 * Shows a short confirmation message at the bottom of the viewport.
 * @param {string} message
 */
export function hxToast(message) {
  if (!message) return;

  let toast = document.querySelector('.hx-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'hx-toast';
    toast.setAttribute('role', 'status');
    document.body.appendChild(toast);
  }

  toast.textContent = message;
  // Force a reflow so the transition replays when two toasts follow each other.
  void (/** @type {HTMLElement} */ (toast).offsetWidth);
  toast.classList.add('is-visible');

  clearTimeout(toastTimeout);
  toastTimeout = setTimeout(() => toast?.classList.remove('is-visible'), 2600);
}

// hx-commerce.js reports add-to-cart errors through the same toast.
window.hxToast = hxToast;

/* -------------------------------------------------------------------------- */
/* Dialog helpers                                                             */
/* -------------------------------------------------------------------------- */

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const closeButton = event.target.closest('[data-hx-close]');
  if (closeButton) {
    closeButton.closest('dialog')?.close();
    return;
  }

  // A click that lands on the dialog element itself is a click on its backdrop.
  if (event.target instanceof HTMLDialogElement && event.target.classList.contains('hx-dialog')) {
    event.target.close();
  }
});

/* -------------------------------------------------------------------------- */
/* Wishlist                                                                   */
/* -------------------------------------------------------------------------- */

const WISHLIST_KEY = 'hx:wishlist';

/**
 * @typedef {{ handle: string, title: string, url: string, image: string, price: string }} WishlistItem
 */

const wishlist = {
  /** @returns {WishlistItem[]} */
  get items() {
    const items = readStorage(localStorage, WISHLIST_KEY, []);
    return Array.isArray(items) ? items.filter((item) => item && typeof item.handle === 'string') : [];
  },

  /** @param {WishlistItem[]} items */
  set items(items) {
    writeStorage(localStorage, WISHLIST_KEY, items);
  },

  /** @param {string} handle */
  has(handle) {
    return this.items.some((item) => item.handle === handle);
  },

  /** @param {WishlistItem} item */
  toggle(item) {
    const items = this.items;
    const index = items.findIndex((entry) => entry.handle === item.handle);
    if (index === -1) items.push(item);
    else items.splice(index, 1);
    this.items = items;
    return index === -1;
  },

  /** @param {string} handle */
  remove(handle) {
    this.items = this.items.filter((item) => item.handle !== handle);
  },

  /**
   * Keeps the stored title, price and image current with what the page just rendered.
   * @param {WishlistItem} snapshot
   */
  refresh(snapshot) {
    const items = this.items;
    const index = items.findIndex((entry) => entry.handle === snapshot.handle);
    if (index === -1) return;
    const current = items[index];
    if (current && current.price === snapshot.price && current.title === snapshot.title) return;
    items[index] = snapshot;
    this.items = items;
  },
};

/**
 * @param {HTMLElement} button
 * @returns {WishlistItem}
 */
function snapshotFromButton(button) {
  return {
    handle: button.dataset.handle ?? '',
    title: button.dataset.title ?? '',
    url: button.dataset.url ?? '',
    image: button.dataset.image ?? '',
    price: button.dataset.price ?? '',
  };
}

function syncWishlistUi() {
  const items = wishlist.items;
  const handles = new Set(items.map((item) => item.handle));

  for (const button of document.querySelectorAll('[data-hx-wishlist-toggle]')) {
    if (!(button instanceof HTMLElement)) continue;
    const active = handles.has(button.dataset.handle ?? '');
    if (button.getAttribute('aria-pressed') !== String(active)) {
      button.setAttribute('aria-pressed', String(active));
    }
    if (active) wishlist.refresh(snapshotFromButton(button));
  }

  for (const counter of document.querySelectorAll('[data-hx-wishlist-count]')) {
    if (!(counter instanceof HTMLElement)) continue;
    const text = items.length > 0 ? String(items.length) : '';
    if (counter.textContent !== text) counter.textContent = text;
    counter.hidden = items.length === 0;
  }
}

function renderWishlistDrawer() {
  const list = document.querySelector('[data-hx-wishlist-list]');
  if (!(list instanceof HTMLElement)) return;

  const items = wishlist.items;
  list.replaceChildren();

  if (items.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'hx-wishlist__empty';
    empty.textContent = list.dataset.emptyText ?? '';
    list.appendChild(empty);
    return;
  }

  for (const item of items) {
    const row = document.createElement('div');
    row.className = 'hx-wishlist__item';

    const link = document.createElement('a');
    link.className = 'hx-wishlist__media';
    link.href = item.url;
    if (item.image) {
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = '';
      image.loading = 'lazy';
      image.width = 80;
      image.height = 80;
      link.appendChild(image);
    }

    const info = document.createElement('div');
    info.className = 'hx-wishlist__info';

    const title = document.createElement('a');
    title.className = 'hx-wishlist__title';
    title.href = item.url;
    title.textContent = item.title;

    const price = document.createElement('span');
    price.className = 'hx-wishlist__price';
    price.textContent = item.price;

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'hx-wishlist__remove button-unstyled';
    remove.dataset.hxWishlistRemove = item.handle;
    remove.textContent = list.dataset.removeText ?? '';

    info.append(title, price, remove);
    row.append(link, info);
    list.appendChild(row);
  }
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const toggle = event.target.closest('[data-hx-wishlist-toggle]');
  if (toggle instanceof HTMLElement) {
    // The button can sit on top of a product card link.
    event.preventDefault();
    event.stopPropagation();

    const added = wishlist.toggle(snapshotFromButton(toggle));
    syncWishlistUi();
    hxToast((added ? toggle.dataset.addedText : toggle.dataset.removedText) ?? '');
    return;
  }

  const opener = event.target.closest('[data-hx-wishlist-open]');
  if (opener) {
    const dialog = document.getElementById('hx-wishlist-drawer');
    if (dialog instanceof HTMLDialogElement) {
      renderWishlistDrawer();
      if (!dialog.open) dialog.showModal();
    }
    return;
  }

  const remove = event.target.closest('[data-hx-wishlist-remove]');
  if (remove instanceof HTMLElement) {
    wishlist.remove(remove.dataset.hxWishlistRemove ?? '');
    renderWishlistDrawer();
    syncWishlistUi();
  }
});

// Another tab changed the list.
window.addEventListener('storage', (event) => {
  if (event.key === WISHLIST_KEY) syncWishlistUi();
});

/** @type {number | undefined} */
let wishlistSyncTimeout;

function scheduleWishlistSync() {
  clearTimeout(wishlistSyncTimeout);
  wishlistSyncTimeout = setTimeout(syncWishlistUi, 150);
}

function initWishlist() {
  if (!document.getElementById('hx-wishlist-drawer') && !document.querySelector('[data-hx-wishlist-toggle]')) return;

  syncWishlistUi();

  // Product cards arrive later through pagination, quick add and section re-renders,
  // and a morph resets `aria-pressed` to the value the server rendered.
  new MutationObserver(scheduleWishlistSync).observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initWishlist);
else initWishlist();

/* -------------------------------------------------------------------------- */
/* Countdown                                                                  */
/* -------------------------------------------------------------------------- */

class HxCountdown extends HTMLElement {
  /** @type {number | undefined} */
  interval;

  /** @type {number} */
  end = 0;

  connectedCallback() {
    this.end = this.resolveEnd();
    this.tick();
    this.interval = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  /** @returns {number} End of the countdown as a millisecond timestamp. */
  resolveEnd() {
    const evergreenMinutes = Number(this.dataset.evergreen);

    if (evergreenMinutes > 0) {
      const key = `hx:countdown:${this.dataset.key}:${evergreenMinutes}`;
      let end = Number(readStorage(localStorage, key, 0));
      const expired = end > 0 && end <= Date.now();

      if (!end || (expired && this.dataset.restart === 'true')) {
        end = Date.now() + evergreenMinutes * 60000;
        writeStorage(localStorage, key, end);
      }

      return end;
    }

    return Number(this.dataset.end) * 1000;
  }

  tick() {
    const remaining = Math.max(0, this.end - Date.now());
    const seconds = Math.floor(remaining / 1000);
    // Without a days unit, the hours carry the whole remaining time.
    const showsDays = this.querySelector('[data-unit="d"]') !== null;
    const units = {
      d: Math.floor(seconds / 86400),
      h: showsDays ? Math.floor((seconds % 86400) / 3600) : Math.floor(seconds / 3600),
      m: Math.floor((seconds % 3600) / 60),
      s: seconds % 60,
    };

    for (const [unit, value] of Object.entries(units)) {
      const element = this.querySelector(`[data-unit="${unit}"]`);
      if (element) element.textContent = String(value).padStart(2, '0');
    }

    if (remaining > 0) return;

    clearInterval(this.interval);
    this.toggleAttribute('data-finished', true);

    // In the editor the block stays visible so the merchant can still select it.
    if (this.dataset.onEnd === 'hide' && !isDesignMode()) this.hidden = true;
  }
}

define('hx-countdown', HxCountdown);

/* -------------------------------------------------------------------------- */
/* Tabs                                                                       */
/* -------------------------------------------------------------------------- */

class HxTabs extends HTMLElement {
  connectedCallback() {
    this.build();
    this.addEventListener('shopify:block:select', this.onBlockSelect);
  }

  disconnectedCallback() {
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  /** @returns {HTMLElement[]} */
  get panels() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-hx-tab]'))).filter(
      (panel) => panel.closest('hx-tabs') === this
    );
  }

  build() {
    const list = this.querySelector('.hx-tabs__list');
    const panels = this.panels;
    if (!list || panels.length === 0) return;

    list.replaceChildren();

    panels.forEach((panel, index) => {
      const id = panel.id || `${this.id}-panel-${index}`;
      panel.id = id;
      panel.setAttribute('role', 'tabpanel');

      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'hx-tabs__tab button-unstyled';
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', id);
      tab.textContent = panel.dataset.title ?? '';
      tab.addEventListener('click', () => this.select(index));
      tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
      list.appendChild(tab);
    });

    this.classList.add('is-enhanced');
    this.select(0);
  }

  /**
   * @param {number} index
   * @param {boolean} [focus]
   */
  select(index, focus = false) {
    const tabs = Array.from(this.querySelectorAll('.hx-tabs__list > [role="tab"]'));

    this.panels.forEach((panel, panelIndex) => {
      const active = panelIndex === index;
      panel.hidden = !active;

      const tab = tabs[panelIndex];
      if (!(tab instanceof HTMLElement)) return;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      if (active && focus) tab.focus();
    });
  }

  /**
   * @param {KeyboardEvent} event
   * @param {number} index
   */
  onKeydown(event, index) {
    const count = this.panels.length;
    if (event.key === 'ArrowRight') this.select((index + 1) % count, true);
    else if (event.key === 'ArrowLeft') this.select((index - 1 + count) % count, true);
    else return;
    event.preventDefault();
  }

  /** @param {Event} event */
  onBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;
    const panel = event.target.closest('[data-hx-tab]');
    const index = panel instanceof HTMLElement ? this.panels.indexOf(panel) : -1;
    if (index !== -1) this.select(index);
  };
}

define('hx-tabs', HxTabs);

/* -------------------------------------------------------------------------- */
/* Stories                                                                    */
/* -------------------------------------------------------------------------- */

const DEFAULT_STORY_DURATION = 5;

class HxStories extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  viewer = null;

  /** @type {number} */
  index = 0;

  connectedCallback() {
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.viewer?.remove();
    this.viewer = null;
  }

  /** @returns {HTMLElement[]} */
  get stories() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-hx-story]')));
  }

  /** @param {MouseEvent} event */
  onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const story = event.target.closest('[data-hx-story]');
    if (!(story instanceof HTMLElement)) return;
    this.open(this.stories.indexOf(story));
  };

  /** @returns {HTMLDialogElement} */
  ensureViewer() {
    if (this.viewer) return this.viewer;

    const viewer = document.createElement('dialog');
    viewer.className = 'hx-story-viewer hx-dialog';
    viewer.innerHTML = `
      <div class="hx-story-viewer__frame">
        <div class="hx-story-viewer__bars"></div>
        <div class="hx-story-viewer__header">
          <span class="hx-story-viewer__title"></span>
          <button type="button" class="hx-story-viewer__close button-unstyled" data-hx-close>&times;</button>
        </div>
        <div class="hx-story-viewer__media"></div>
        <button type="button" class="hx-story-viewer__nav hx-story-viewer__nav--previous button-unstyled" data-direction="-1"></button>
        <button type="button" class="hx-story-viewer__nav hx-story-viewer__nav--next button-unstyled" data-direction="1"></button>
        <a class="hx-story-viewer__link button" hidden></a>
      </div>
    `;

    viewer.querySelector('.hx-story-viewer__close')?.setAttribute('aria-label', this.dataset.closeLabel ?? '');

    for (const nav of viewer.querySelectorAll('.hx-story-viewer__nav')) {
      if (!(nav instanceof HTMLElement)) continue;
      nav.setAttribute(
        'aria-label',
        (nav.dataset.direction === '1' ? this.dataset.nextLabel : this.dataset.previousLabel) ?? ''
      );
      nav.addEventListener('click', () => this.show(this.index + Number(nav.dataset.direction)));
    }

    viewer.addEventListener('close', () => this.clearMedia());

    document.body.appendChild(viewer);
    this.viewer = viewer;
    return viewer;
  }

  /** @param {number} index */
  open(index) {
    const viewer = this.ensureViewer();
    const bars = viewer.querySelector('.hx-story-viewer__bars');

    if (bars) {
      bars.replaceChildren(
        ...this.stories.map(() => {
          const bar = document.createElement('span');
          bar.className = 'hx-story-viewer__bar';
          bar.appendChild(document.createElement('span'));
          return bar;
        })
      );
    }

    if (!viewer.open) viewer.showModal();
    this.show(index);
  }

  clearMedia() {
    const media = this.viewer?.querySelector('.hx-story-viewer__media');
    const video = media?.querySelector('video');
    video?.pause();
    media?.replaceChildren();
  }

  /** @param {number} index */
  show(index) {
    const viewer = this.viewer;
    const stories = this.stories;
    if (!viewer) return;

    if (index >= stories.length) {
      viewer.close();
      return;
    }

    this.index = Math.max(0, index);
    const story = stories[this.index];
    if (!story) return;

    this.clearMedia();

    const title = viewer.querySelector('.hx-story-viewer__title');
    if (title) title.textContent = story.dataset.title ?? '';

    const link = viewer.querySelector('.hx-story-viewer__link');
    if (link instanceof HTMLAnchorElement) {
      link.hidden = !story.dataset.link;
      link.href = story.dataset.link ?? '#';
      link.textContent = story.dataset.linkLabel ?? '';
    }

    const bars = /** @type {HTMLElement[]} */ (Array.from(viewer.querySelectorAll('.hx-story-viewer__bar')));
    bars.forEach((bar, barIndex) => {
      bar.classList.toggle('is-done', barIndex < this.index);
      bar.classList.remove('is-active');
    });

    const activeBar = bars[this.index];
    const fill = activeBar?.firstElementChild;
    const media = viewer.querySelector('.hx-story-viewer__media');
    if (!media || !activeBar || !(fill instanceof HTMLElement)) return;

    /** @param {number} seconds */
    const start = (seconds) => {
      fill.style.animationDuration = `${seconds}s`;
      // Restart the CSS animation for a bar that already played.
      void activeBar.offsetWidth;
      activeBar.classList.add('is-active');
    };

    fill.onanimationend = () => this.show(this.index + 1);

    if (story.dataset.type === 'video' && story.dataset.src) {
      const video = document.createElement('video');
      video.src = story.dataset.src;
      video.playsInline = true;
      video.autoplay = true;
      video.muted = story.dataset.muted !== 'false';
      video.addEventListener(
        'loadedmetadata',
        () => start(Number.isFinite(video.duration) && video.duration > 0 ? video.duration : DEFAULT_STORY_DURATION),
        { once: true }
      );
      media.appendChild(video);
      video.play().catch(() => {
        // Autoplay with sound can be refused; fall back to a muted playback.
        video.muted = true;
        video.play().catch(() => {});
      });
      return;
    }

    const image = document.createElement('img');
    image.src = story.dataset.src ?? '';
    image.alt = story.dataset.title ?? '';
    media.appendChild(image);
    start(Number(story.dataset.duration) || DEFAULT_STORY_DURATION);
  }
}

define('hx-stories', HxStories);

/* -------------------------------------------------------------------------- */
/* Delivery estimate                                                          */
/* -------------------------------------------------------------------------- */

class HxDelivery extends HTMLElement {
  connectedCallback() {
    const output = this.querySelector('[data-output]');
    const template = this.dataset.template ?? '';
    if (!output) return;

    const minimum = Number(this.dataset.min) || 0;
    const maximum = Math.max(minimum, Number(this.dataset.max) || 0);
    const cutoff = Number(this.dataset.cutoff);
    const skipWeekends = this.dataset.skipWeekends === 'true';

    /** @param {Date} date */
    const isWeekend = (date) => date.getDay() === 0 || date.getDay() === 6;

    // Orders placed after the cut-off hour ship the next day.
    const shippingDay = new Date();
    if (cutoff >= 0 && shippingDay.getHours() >= cutoff) shippingDay.setDate(shippingDay.getDate() + 1);
    if (skipWeekends) {
      while (isWeekend(shippingDay)) shippingDay.setDate(shippingDay.getDate() + 1);
    }

    /** @param {number} days */
    const addDays = (days) => {
      const date = new Date(shippingDay);
      let remaining = days;
      while (remaining > 0) {
        date.setDate(date.getDate() + 1);
        if (!skipWeekends || !isWeekend(date)) remaining -= 1;
      }
      return date;
    };

    const formatter = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });

    const dates = {
      '[date_min]': formatter.format(addDays(minimum)),
      '[date_max]': formatter.format(addDays(maximum)),
    };

    output.replaceChildren(
      ...template.split(/(\[date_min\]|\[date_max\])/).map((part) => {
        if (part !== '[date_min]' && part !== '[date_max]') return document.createTextNode(part);
        const strong = document.createElement('strong');
        strong.textContent = dates[part];
        return strong;
      })
    );

    this.hidden = false;
  }
}

define('hx-delivery', HxDelivery);

/* -------------------------------------------------------------------------- */
/* Text slider                                                                */
/* -------------------------------------------------------------------------- */

class HxTextSlider extends HTMLElement {
  /** @type {number | undefined} */
  interval;

  connectedCallback() {
    const items = Array.from(this.querySelectorAll('.hx-text-slider__item'));
    if (items.length === 0) return;

    let index = 0;
    items.forEach((item, itemIndex) => item.classList.toggle('is-active', itemIndex === 0));
    if (items.length < 2) return;

    const seconds = Number(this.dataset.interval) || 4;
    this.interval = setInterval(() => {
      items[index]?.classList.remove('is-active');
      index = (index + 1) % items.length;
      items[index]?.classList.add('is-active');
    }, seconds * 1000);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }
}

define('hx-text-slider', HxTextSlider);

/* -------------------------------------------------------------------------- */
/* Cart reservation timer                                                     */
/* -------------------------------------------------------------------------- */

const CART_TIMER_KEY = 'hx:cart-timer:end';

class HxCartTimer extends HTMLElement {
  /** @type {number | undefined} */
  interval;

  /** @type {number} */
  end = 0;

  /** @type {boolean} */
  clearing = false;

  connectedCallback() {
    this.end = Number(readStorage(sessionStorage, CART_TIMER_KEY, 0));

    // A timer that ran out on a previous page starts over with the new visit of the cart.
    if (!this.end || this.end <= Date.now()) {
      this.end = Date.now() + (Number(this.dataset.minutes) || 10) * 60000;
      writeStorage(sessionStorage, CART_TIMER_KEY, this.end);
    }

    this.tick();
    this.interval = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  tick() {
    // The cart section is morphed after each cart change, which restores the server
    // markup, so the three nodes are looked up and written on every tick.
    const remaining = Math.max(0, this.end - Date.now());
    const seconds = Math.floor(remaining / 1000);
    const output = this.querySelector('[data-timer]');
    const running = this.querySelector('[data-running]');
    const expired = this.querySelector('[data-expired]');

    if (output) {
      output.textContent = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
    }
    if (running instanceof HTMLElement) running.hidden = remaining === 0;
    if (expired instanceof HTMLElement) expired.hidden = remaining > 0;

    if (remaining > 0 || this.clearing || this.dataset.clear !== 'true' || isDesignMode()) return;

    this.clearing = true;
    clearInterval(this.interval);
    try {
      sessionStorage.removeItem(CART_TIMER_KEY);
    } catch (_) {
      // Nothing to clean up when storage is unavailable.
    }

    fetch(`${window.Theme?.routes?.cart_url ?? '/cart'}/clear.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(() => window.location.reload())
      .catch((error) => console.warn('[hx-cart-timer] Could not clear the cart:', error));
  }
}

define('hx-cart-timer', HxCartTimer);

/* -------------------------------------------------------------------------- */
/* Promo popup                                                                */
/* -------------------------------------------------------------------------- */

class HxPopup extends HTMLElement {
  /** @type {number | undefined} */
  timeout;

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    if (!dialog) return;

    if (isDesignMode()) {
      // In the editor the popup follows the section selection instead of the delay.
      this.onSectionSelect = () => {
        if (!dialog.open) dialog.show();
      };
      this.onSectionDeselect = () => dialog.close();
      this.closest('.shopify-section')?.addEventListener('shopify:section:select', this.onSectionSelect);
      this.closest('.shopify-section')?.addEventListener('shopify:section:deselect', this.onSectionDeselect);
      return;
    }

    const key = `hx:popup:${this.dataset.key}`;

    // The newsletter form was just submitted from this popup: show its result.
    if (this.querySelector('[data-hx-popup-result]')) {
      dialog.showModal();
      writeStorage(localStorage, key, Date.now());
      return;
    }

    const days = Number(this.dataset.days) || 0;
    const lastShown = Number(readStorage(localStorage, key, 0));
    if (lastShown && Date.now() - lastShown < days * 86400000) return;

    this.timeout = setTimeout(() => {
      // Never stack on top of another open modal (cart drawer, quick add, search).
      if (document.querySelector('dialog:modal')) return;
      dialog.showModal();
      writeStorage(localStorage, key, Date.now());
    }, (Number(this.dataset.delay) || 0) * 1000);
  }

  disconnectedCallback() {
    clearTimeout(this.timeout);
  }
}

define('hx-popup', HxPopup);
